type OperationRecord = Record<string, unknown> | null | undefined;
type ResultRecord = Record<string, unknown> | null | undefined;

interface RemoteRefreshGateParams {
  operation: OperationRecord;
  resultRecord: ResultRecord;
  isRemoteSession: boolean;
  refreshProjectAvailable: boolean;
  touchesVisibilityMutation: boolean;
  operationSupported: boolean;
}

export type RemoteRefreshReason =
  | 'remote-unsupported-mutation'
  | 'remote-mutation-fallback'
  | 'remote-visibility-mutation'
  | null;

const VISIBILITY_MUTATION_KEYS = [
  'visibilityTier',
  'visibility_tier',
  'visibleToAccountIds',
  'visible_to_account_ids',
  'displayVisibility',
  'display_visibility',
];

function hasNonEmptyArray(value: unknown): boolean {
  return Array.isArray(value) && value.length > 0;
}

function hasObject(value: unknown): boolean {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function hasAuthoritativeUpdatedNodes(operation: OperationRecord, resultRecord: ResultRecord): boolean {
  return (
    hasNonEmptyArray(resultRecord?.updatedNodesFull) ||
    hasNonEmptyArray(operation?.updatedNodes)
  );
}

function hasSiblingOrder(operation: OperationRecord): boolean {
  return hasNonEmptyArray(operation?.nodeIds);
}

function hasSourceSiblingOrder(operation: OperationRecord): boolean {
  return operation?.sourceParentId !== undefined && hasNonEmptyArray(operation?.sourceNodeIds);
}

function recordTouchesVisibilityMutation(record: unknown): boolean {
  if (!record || typeof record !== 'object' || Array.isArray(record)) {
    return false;
  }

  const recordMap = record as Record<string, unknown>;
  if (VISIBILITY_MUTATION_KEYS.some((key) => Object.prototype.hasOwnProperty.call(recordMap, key))) {
    return true;
  }

  const customFields =
    (recordMap.customFields && typeof recordMap.customFields === 'object' && !Array.isArray(recordMap.customFields)
      ? (recordMap.customFields as Record<string, unknown>)
      : null) ||
    (recordMap.custom_fields && typeof recordMap.custom_fields === 'object' && !Array.isArray(recordMap.custom_fields)
      ? (recordMap.custom_fields as Record<string, unknown>)
      : null);

  return Boolean(
    customFields &&
    VISIBILITY_MUTATION_KEYS.some((key) => Object.prototype.hasOwnProperty.call(customFields, key))
  );
}

function refreshHintMentionsVisibility(hint: unknown): boolean {
  if (!hint) {
    return false;
  }

  if (typeof hint === 'string') {
    return hint.toLowerCase().includes('visibility');
  }

  if (Array.isArray(hint)) {
    return hint.some(refreshHintMentionsVisibility);
  }

  if (typeof hint !== 'object') {
    return false;
  }

  const hintRecord = hint as Record<string, unknown>;
  const semanticValues = [
    hintRecord.reason,
    hintRecord.type,
    hintRecord.kind,
    hintRecord.scope,
  ];
  if (semanticValues.some((value) => typeof value === 'string' && value.toLowerCase().includes('visibility'))) {
    return true;
  }

  return refreshHintMentionsVisibility(hintRecord.fields);
}

function resultHasVisibilityRefreshHint(resultRecord: ResultRecord): boolean {
  return (
    refreshHintMentionsVisibility(resultRecord?.refreshHint) ||
    refreshHintMentionsVisibility(resultRecord?.refresh_hint)
  );
}

export function operationPayloadTouchesVisibilityMutation(operation: OperationRecord): boolean {
  if (recordTouchesVisibilityMutation(operation?.data)) {
    return true;
  }

  const dataRecord = operation?.data && typeof operation.data === 'object' && !Array.isArray(operation.data)
    ? operation.data as Record<string, unknown>
    : null;
  const candidateLists = [
    operation?.updates,
    dataRecord?.updates,
  ];

  return candidateLists.some((candidate) => {
    if (!Array.isArray(candidate)) {
      return false;
    }

    return candidate.some((entry) => {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
        return false;
      }
      const entryRecord = entry as Record<string, unknown>;
      return (
        recordTouchesVisibilityMutation(entryRecord.data) ||
        recordTouchesVisibilityMutation(entryRecord.operation)
      );
    });
  });
}

export function operationResultTouchesVisibilityMutation(
  operation: OperationRecord,
  resultRecord: ResultRecord
): boolean {
  return resultHasVisibilityRefreshHint(resultRecord) || operationPayloadTouchesVisibilityMutation(operation);
}

export function operationResultHasAuthoritativePayload(
  operation: OperationRecord,
  resultRecord: ResultRecord
): boolean {
  const operationType = typeof operation?.type === 'string' ? operation.type : '';

  if (!operationType) {
    return false;
  }

  if (hasObject(resultRecord?.node) || hasAuthoritativeUpdatedNodes(operation, resultRecord)) {
    return true;
  }

  if (operationType === 'progress-update') {
    return typeof operation?.progress === 'number';
  }

  if (operationType === 'move') {
    return hasSiblingOrder(operation) || hasSourceSiblingOrder(operation);
  }

  if (operationType === 'reorder') {
    return hasSiblingOrder(operation);
  }

  return false;
}

export function getRemoteRefreshReason({
  operation,
  resultRecord,
  isRemoteSession,
  refreshProjectAvailable,
  touchesVisibilityMutation,
  operationSupported,
}: RemoteRefreshGateParams): RemoteRefreshReason {
  if (!isRemoteSession || !refreshProjectAvailable) {
    return null;
  }

  if (!operationSupported) {
    return 'remote-unsupported-mutation';
  }

  if (touchesVisibilityMutation) {
    return 'remote-visibility-mutation';
  }

  const hasAuthoritativePayload = operationResultHasAuthoritativePayload(operation, resultRecord);
  if (!hasAuthoritativePayload) {
    return 'remote-mutation-fallback';
  }

  return null;
}
